import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  InputAdornment,
  Avatar,
  TablePagination,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Verified as VerifiedIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  Language as WebIcon,
  LinkedIn as LinkedInIcon,
  PlayArrow as PlayArrowIcon,
  History as HistoryIcon,
  SmartToy as RobotIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../../services/api';
import { operationalBrandColors } from '../../../../design-system/tokens';

// Ícone customizado para TikTok (não existe no MUI)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Ícone customizado para Facebook
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const CronPanel = () => {
  const theme = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile-followers/cron/logs');
      setLogs(response.data.data);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const handleTrigger = async () => {
    try {
      setTriggering(true);
      setError('');
      setSuccess('');
      await api.post('/profile-followers/cron/trigger');
      setSuccess('Robô iniciado com sucesso! Aguarde novas atualizações no histórico.');
      setTimeout(() => {
        fetchLogs();
      }, 2000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Erro ao iniciar robô');
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 10000); // Auto refresh every 10s
    return () => clearInterval(interval);
  }, []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">Automação de Atualização</Typography>
          <Typography variant="body2" color="text.secondary">
            Histórico de execuções do robô de métricas
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={triggering ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleTrigger}
          disabled={triggering}
        >
          {triggering ? 'Iniciando...' : 'Executar Agora'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Processados</TableCell>
                <TableCell align="right">Sucessos</TableCell>
                <TableCell align="right">Erros</TableCell>
                <TableCell>Log Detalhado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell>
                    {new Date(log.startTime).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.status === 'running' ? 'Rodando...' : log.status}
                      color={log.status === 'success' ? 'success' : log.status === 'running' ? 'warning' : 'error'}
                      size="small"
                      icon={log.status === 'running' ? <CircularProgress size={12} color="inherit" /> :
                        log.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                    />
                  </TableCell>
                  <TableCell align="right">{log.profilesProcessed}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{log.successCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{log.errorCount}</TableCell>
                  <TableCell>
                    <Tooltip title={
                      <div style={{ whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto' }}>
                        {log.logs?.join('\n')}
                      </div>
                    }>
                      <Button size="small">Ver Logs</Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">Nenhum histórico encontrado</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

const ProfileFollowersManagement = () => {
  const theme = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  // Filtros e paginação
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalProfiles, setTotalProfiles] = useState(0);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    platform: 'Instagram',
    username: '',
    followersNumber: '',
    followersText: '',
    profileName: '',
    profileAvatar: '',
    profileUrl: '',
    isVerified: false
  });

  const platforms = ['Instagram', 'TikTok', 'Twitter', 'YouTube', 'Facebook', 'LinkedIn'];

  const getPlatformIcon = (platform) => {
    const iconProps = { sx: { fontSize: '1.2rem' } };
    const icons = {
      Instagram: <InstagramIcon {...iconProps} sx={{ ...iconProps.sx, color: '#E4405F' }} />,
      YouTube: <YouTubeIcon {...iconProps} sx={{ ...iconProps.sx, color: operationalBrandColors.youtube }} />,
      Twitter: <XIcon {...iconProps} />,
      TikTok: <TikTokIcon style={{ fontSize: '1.2rem' }} />,
      Facebook: <FacebookIcon style={{ fontSize: '1.2rem', color: '#1877F2' }} />,
      LinkedIn: <LinkedInIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0A66C2' }} />
    };
    return icons[platform] || <WebIcon {...iconProps} />;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'lastUpdated',
        sortOrder: 'desc'
      });

      if (selectedPlatform) {
        params.append('platform', selectedPlatform);
      }

      const response = await api.get(`/profile-followers?${params}`);
      setProfiles(response.data.data);
      setTotalProfiles(response.data.pagination.total);
      setError('');
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
      setError('Erro ao carregar perfis: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/profile-followers/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  useEffect(() => {
    fetchProfiles();
    fetchStats();
  }, [page, rowsPerPage, selectedPlatform]);

  const handleEditClick = (profile) => {
    setSelectedProfile(profile);
    setFormData({
      platform: profile.platform,
      username: profile.username,
      followersNumber: profile.followersNumber?.toString() || '',
      followersText: profile.followersText || '',
      profileName: profile.profileName || '',
      profileAvatar: profile.profileAvatar || '',
      profileUrl: profile.profileUrl || '',
      isVerified: profile.isVerified || false
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (profile) => {
    setSelectedProfile(profile);
    setDeleteDialogOpen(true);
  };

  const handleAddClick = () => {
    setFormData({
      platform: 'Instagram',
      username: '',
      followersNumber: '',
      followersText: '',
      profileName: '',
      profileAvatar: '',
      profileUrl: '',
      isVerified: false
    });
    setAddDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      const payload = {
        ...formData,
        followersNumber: parseInt(formData.followersNumber) || 0
      };

      await api.post('/profile-followers', payload);

      setSuccess('Perfil salvo com sucesso!');
      setEditDialogOpen(false);
      setAddDialogOpen(false);
      fetchProfiles();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      setError('Erro ao salvar perfil: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteProfile = async () => {
    try {
      await api.delete(`/profile-followers/${selectedProfile.platform}/${selectedProfile.username}`);

      setSuccess('Perfil excluído com sucesso!');
      setDeleteDialogOpen(false);
      fetchProfiles();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      setError('Erro ao excluir perfil: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSearchByUrl = async () => {
    if (!searchQuery) return;

    try {
      setLoading(true);
      const response = await api.get(`/profile-followers/by-url?url=${encodeURIComponent(searchQuery)}`);

      if (response.data.data) {
        setProfiles([response.data.data]);
        setTotalProfiles(1);
      } else {
        setProfiles([]);
        setTotalProfiles(0);
        setError('Nenhum perfil encontrado para esta URL');
      }
    } catch (error) {
      console.error('Erro ao buscar por URL:', error);
      setError('Erro ao buscar por URL: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    fetchProfiles();
  };

  if (loading && profiles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Cache de Seguidores
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie cache de seguidores por perfil
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Adicionar Perfil
        </Button>
      </Box>

      <Tabs
        value={currentTab}
        onChange={(e, v) => setCurrentTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<PeopleIcon />} label="Cache de Perfis" iconPosition="start" />
        <Tab icon={<RobotIcon />} label="Automação / Robô" iconPosition="start" />
      </Tabs>

      {currentTab === 1 && <CronPanel />}

      {currentTab === 0 && (
        <>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Cards de estatísticas */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2}>
                <Card sx={{ backgroundColor: theme.palette.background.paper, border: 1, borderColor: 'divider' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total de Perfis
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {stats.total || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {Object.entries(stats.byPlatform || {}).map(([platform, data]) => (
                <Grid item xs={6} sm={4} md key={platform}>
                  <Card sx={{ backgroundColor: theme.palette.background.paper, border: 1, borderColor: 'divider' }}>
                    <CardContent sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getPlatformIcon(platform)}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {platform}
                        </Typography>
                      </Box>
                      <Typography variant="h5" color="text.primary">
                        {data.count}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.fallbackUsage} fallbacks
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Filtros */}
          <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, border: 1, borderColor: 'divider' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar por URL do perfil..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchByUrl()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <Button size="small" onClick={handleClearSearch}>
                            Limpar
                          </Button>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Plataforma"
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {platforms.map((platform) => (
                      <MenuItem key={platform} value={platform}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getPlatformIcon(platform)} {platform}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleSearchByUrl}
                    disabled={!searchQuery}
                    fullWidth
                  >
                    Buscar
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => { fetchProfiles(); fetchStats(); }}
                    disabled={loading}
                    fullWidth
                  >
                    Atualizar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabela de perfis */}
          <Card sx={{ backgroundColor: theme.palette.background.paper, border: 1, borderColor: 'divider' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Perfil</TableCell>
                    <TableCell>Plataforma</TableCell>
                    <TableCell align="right">Seguidores</TableCell>
                    <TableCell align="center">Verificado</TableCell>
                    <TableCell align="right">Fallbacks</TableCell>
                    <TableCell align="right">Atualizado</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profiles.map((profile) => (
                    <TableRow key={`${profile.platform}-${profile.username}`} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            src={profile.profileAvatar}
                            sx={{ width: 36, height: 36 }}
                          >
                            {profile.username?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              @{profile.username}
                            </Typography>
                            {profile.profileName && (
                              <Typography variant="caption" color="text.secondary">
                                {profile.profileName}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>{getPlatformIcon(profile.platform)}</Box>}
                          label={profile.platform}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="medium" color="primary">
                          {formatNumber(profile.followersNumber)}
                        </Typography>
                        {profile.followersText && (
                          <Typography variant="caption" color="text.secondary">
                            ({profile.followersText})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {profile.isVerified && (
                          <VerifiedIcon color="primary" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={profile.fallbackUsageCount || 0}
                          size="small"
                          color={profile.fallbackUsageCount > 10 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {new Date(profile.lastUpdated).toLocaleDateString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(profile)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(profile)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {profiles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum perfil encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalProfiles}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Card>

          {/* Dialog de edição/adição */}
          <PlatformDialog
            open={editDialogOpen || addDialogOpen}
            onClose={() => { setEditDialogOpen(false); setAddDialogOpen(false); }}
            maxWidth="sm"
            fullWidth
          >
            <PlatformDialogTitle 
              title={editDialogOpen ? `Editar Perfil — @${selectedProfile?.username}` : 'Novo Perfil no Cache'}
              subtitle={editDialogOpen ? 'Ajuste os dados manuais da fonte social.' : 'Adicione uma nova fonte para cache de métricas manuais.'}
              icon={editDialogOpen ? getPlatformIcon(selectedProfile?.platform) : <AddIcon />}
              onClose={() => { setEditDialogOpen(false); setAddDialogOpen(false); }}
            />
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <FormControl fullWidth size="small" disabled={editDialogOpen}>
                  <InputLabel>Plataforma Social</InputLabel>
                  <Select
                    value={formData.platform}
                    label="Plataforma Social"
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    {platforms.map((platform) => (
                      <MenuItem key={platform} value={platform}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {getPlatformIcon(platform)} 
                          <Typography variant="body2">{platform}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Username (Identificador Único)"
                  size="small"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  disabled={editDialogOpen}
                  placeholder="ex: neymarjr"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Número Real de Seguidores"
                      size="small"
                      type="number"
                      value={formData.followersNumber}
                      onChange={(e) => setFormData({ ...formData, followersNumber: e.target.value })}
                      placeholder="150000000"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Texto Exibido (Label)"
                      size="small"
                      value={formData.followersText}
                      onChange={(e) => setFormData({ ...formData, followersText: e.target.value })}
                      placeholder="150M"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Nome Amigável / Descrição"
                  size="small"
                  value={formData.profileName}
                  onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
                  placeholder="Neymar Jr"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  label="URL do Avatar (Imagem)"
                  size="small"
                  value={formData.profileAvatar}
                  onChange={(e) => setFormData({ ...formData, profileAvatar: e.target.value })}
                  placeholder="https://..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  label="URL Direta do Perfil"
                  size="small"
                  value={formData.profileUrl}
                  onChange={(e) => setFormData({ ...formData, profileUrl: e.target.value })}
                  placeholder="https://instagram.com/neymarjr"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button onClick={() => { setEditDialogOpen(false); setAddDialogOpen(false); }} sx={{ fontWeight: 600 }}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveProfile}
                variant="contained"
                disabled={!formData.username || !formData.followersNumber}
                sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}
              >
                Salvar Perfil
              </Button>
            </DialogActions>
          </PlatformDialog>

          {/* Dialog de confirmação de exclusão */}
          <PlatformDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
            <PlatformDialogTitle 
              title="Confirmar Exclusão"
              subtitle={`Ação permanente para @${selectedProfile?.username}`}
              icon={<WarningIcon />}
              onClose={() => setDeleteDialogOpen(false)}
              error
            />
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="body1" fontWeight={500}>
                Tem certeza que deseja excluir o perfil <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>@{selectedProfile?.username}</Box> ({selectedProfile?.platform})?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Esta ação invalidará o cache de métricas deste usuário no sistema.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
              <Button 
                onClick={handleDeleteProfile} 
                variant="contained" 
                color="error"
                sx={{ fontWeight: 700, borderRadius: 2 }}
              >
                Excluir de Vez
              </Button>
            </DialogActions>
          </PlatformDialog>
        </>
      )}
    </Box>
  );
};

export default ProfileFollowersManagement;
